import React from 'react';
import { Navigate } from 'react-router-dom';

import SubmittedScheduleFullComponent from '../components/submission/submitted-schedule/SubmittedScheduleFullComponent';
import GeneratedScheduleFullComponent from '../components/schedule/generated-schedule/GeneratedScheduleFullComponent';
import EmailTriggerComponent from '../components/email-trigger/EmailTriggerComponent';

import ChannelArchitecture from '../components/architecture/ChannelArchitecture';
import BreakPatterns from '../components/break-patterns/BreakPatterns';
import Playlist from 'Playlist/Playlist';
import ProtectedRoute from './ProtectedRoute';
import { unifiedScheduleConfigurations } from 'components/unified-schedule/helpers/config';
import { lazyWithRetry } from 'utils/lazyWithRetry';

const AccessDeniedComponent = lazyWithRetry(() =>
  import('../components/_error-pages/access-denied/AccessDeniedComponent'),
);
const HomeComponent = lazyWithRetry(() =>
  import('../components/home/HomeComponent'),
);
const InternalServerComponent = lazyWithRetry(() =>
  import('../components/_error-pages/internal-server/InternalServerComponent'),
);
const NotFoundComponent = lazyWithRetry(() =>
  import('../components/_error-pages/not-found/NotFoundComponent'),
);
const MaintenanceComponent = lazyWithRetry(() =>
  import('../components/maintenance/MaintenanceComponent'),
);
const ChannelProfileComponent = lazyWithRetry(() =>
  import('../components/channel-profiles/ChannelProfileComponent'),
);
const PrioritisationComponent = lazyWithRetry(() =>
  import('../components/prioritisation/PrioritisationComponent'),
);
const ScheduleComponent = lazyWithRetry(() =>
  import('../components/pages/scheduler/ScheduleComponent'),
);

const SubmissionComponent = lazyWithRetry(() =>
  import('../components/submission/SubmissionComponent'),
);
const TitleGroupComponent = lazyWithRetry(() =>
  import('../components/title-group/TitleGroupComponent'),
);
const HelpComponent = lazyWithRetry(() =>
  import('../components/help/HelpComponent'),
);
const UserProfileComponent = lazyWithRetry(() =>
  import('../components/profile/UserProfileComponent'),
);
const ScheduleDetailsComponent = lazyWithRetry(() =>
  import('../components/schedule/schedule-details/ScheduleDetailsComponent'),
);
const ChannelComponent = lazyWithRetry(() =>
  import('../components/channel/ChannelComponent'),
);
const RequestTimeoutComponent = lazyWithRetry(() =>
  import('../components/_error-pages/request-timeout/RequestTimeoutComponent'),
);
const ChannelGroupComponent = lazyWithRetry(() =>
  import('../components/channel-group/ChannelGroupComponent'),
);
const BusinessRulesComponent = lazyWithRetry(() =>
  import('../components/business-rules/BusinessRulesComponent'),
);
const CustomBusinessRulesComponent = lazyWithRetry(() =>
  import('../components/business-rules-custom/CustomBusinessRulesComponent'),
);
const BusinessRuleDefinitionComponent = lazyWithRetry(() =>
  import(
    '../components/business-rule-definition/BusinessRuleDefinitionComponent'
  ),
);
const BusinessRuleValuesComponent = lazyWithRetry(() =>
  import('../components/business-rule-values/BusinessRuleValuesComponent'),
);
const UserComponent = lazyWithRetry(() =>
  import('../components/user/UserComponent'),
);
const OrganisationComponent = lazyWithRetry(() =>
  import('../components/organisation/OrganisationComponent'),
);
const TitleExclusionComponent = lazyWithRetry(() =>
  import('../components/title-exclusion/TitleExclusionComponent'),
);
const AttributesOverrideComponent = lazyWithRetry(() =>
  import('../components/attributes-override/AttributesOverrideComponent'),
);

const ViewScheduleComponent = lazyWithRetry(() =>
  import('../components/view-schedule/ViewSchedulesComponent'),
);

const InclusionComponent = lazyWithRetry(() =>
  import('../components/inclusion/InclusionComponent'),
);

const ProgramLibraryComponent = lazyWithRetry(() =>
  import('../components/library/Programs/ProgramsComponent'),
);

const RightsComponent = lazyWithRetry(() =>
  import('../components/rights/RightsComponent'),
);

const InterstitialsComponent = lazyWithRetry(() =>
  import('../components/interstitials/InterstitialsComponent'),
);

const ContentTypesComponent = lazyWithRetry(() =>
  import('../components/content-types/ContentTypesComponent'),
);

const CommercialsComponent = lazyWithRetry(() =>
  import('../components/commercials/CommercialsComponent'),
);

const AdvancedScheduleComponent = lazyWithRetry(() =>
  import('../components/advanced-schedule/AdvancedScheduleComponent'),
);

const ExportScheduleComponent = lazyWithRetry(() =>
  import('../components/export-schedule/ExportScheduleComponent'),
);

const StandbyScheduleComponent = lazyWithRetry(() =>
  import('../components/standby-schedule/StandbyScheduleComponent'),
);

const PresentationScheduleComponent = lazyWithRetry(() =>
  import('../components/presentation-schedule/PresentationScheduleComponent'),
);

const NotificationsComponent = lazyWithRetry(() =>
  import('../components/reporting/notifications/NotificationsComponent'),
);

const MonitoringComponent = lazyWithRetry(() =>
  import('../components/reporting/monitoring/MonitoringComponent'),
);

const ReportHistory = lazyWithRetry(() =>
  import('../components/report/history/ReportHistory'),
);

const ReportConfiguration = lazyWithRetry(() =>
  import('../components/report/configuration/ReportConfiguration'),
);

const UnifiedScheduleComponent = lazyWithRetry(() =>
  import('../components/unified-schedule/ScheduleComponent'),
);

class ViewsRouter {
  routes = [
    {
      path: `${process.env.REACT_APP_SUBFOLDER}/`,
      element: <HomeComponent />,
    },
    {
      path: `${process.env.REACT_APP_SUBFOLDER}/architecture`,
      element: <ChannelArchitecture />,
    },
    {
      path: `${process.env.REACT_APP_SUBFOLDER}/break-patterns`,
      element: <BreakPatterns />,
    },
    {
      path: `${process.env.REACT_APP_SUBFOLDER}/business-rules`,
      element: <BusinessRulesComponent />,
    },
    {
      path: `${process.env.REACT_APP_SUBFOLDER}/custom-business-rules`,
      element: <CustomBusinessRulesComponent />,
    },
    {
      path: `${process.env.REACT_APP_SUBFOLDER}/business-rule-definitions`,
      element: <BusinessRuleDefinitionComponent />,
    },
    {
      path: `${process.env.REACT_APP_SUBFOLDER}/business-rule-values`,
      element: <BusinessRuleValuesComponent />,
    },
    {
      path: `${process.env.REACT_APP_SUBFOLDER}/channel-profiles`,
      element: <ChannelProfileComponent />,
    },
    {
      path: `${process.env.REACT_APP_SUBFOLDER}/not-found`,
      element: <NotFoundComponent />,
    },
    {
      path: `${process.env.REACT_APP_SUBFOLDER}/access-denied`,
      element: <AccessDeniedComponent />,
    },
    {
      path: `${process.env.REACT_APP_SUBFOLDER}/internal-server/:message`,
      element: <InternalServerComponent />,
    },
    {
      path: `${process.env.REACT_APP_SUBFOLDER}/prioritisation`,
      element: <PrioritisationComponent />,
    },
    {
      path: `${process.env.REACT_APP_SUBFOLDER}/library/title-groups`,
      element: <TitleGroupComponent />,
    },
    {
      path: `${process.env.REACT_APP_SUBFOLDER}/view-schedules`,
      element: (
        <ProtectedRoute>
          <ViewScheduleComponent />
        </ProtectedRoute>
      ),
    },
    {
      path: `${process.env.REACT_APP_SUBFOLDER}/schedule`,
      element: (
        <ProtectedRoute>
          <ScheduleComponent />
        </ProtectedRoute>
      ),
    },
    {
      path: `${process.env.REACT_APP_SUBFOLDER}/scheduledetails`,
      element: <ScheduleDetailsComponent />,
    },
    {
      path: `${process.env.REACT_APP_SUBFOLDER}/submitted`,
      element: <SubmissionComponent />,
    },
    {
      path: `${process.env.REACT_APP_SUBFOLDER}/export`,
      element: (
        <ProtectedRoute>
          <ExportScheduleComponent />
        </ProtectedRoute>
      ),
    },
    {
      path: `${process.env.REACT_APP_SUBFOLDER}/email-triggers`,
      element: <EmailTriggerComponent />,
    },
    {
      path: `${process.env.REACT_APP_SUBFOLDER}/report/history`,
      element: <ReportHistory />,
    },
    {
      path: `${process.env.REACT_APP_SUBFOLDER}/report/configurations`,
      element: <ReportConfiguration />,
    },
    {
      path: `${process.env.REACT_APP_SUBFOLDER}/help`,
      element: <HelpComponent />,
    },
    {
      path: `${process.env.REACT_APP_SUBFOLDER}/maintenance`,
      element: <MaintenanceComponent />,
    },
    {
      path: `${process.env.REACT_APP_SUBFOLDER}/request-timed-out`,
      element: <RequestTimeoutComponent />,
    },
    {
      path: `${process.env.REACT_APP_SUBFOLDER}/generatedschedulecalendar`,
      element: <GeneratedScheduleFullComponent />,
    },
    {
      path: `${process.env.REACT_APP_SUBFOLDER}/submittedschedules`,
      element: <SubmittedScheduleFullComponent />,
    },
    {
      path: `${process.env.REACT_APP_SUBFOLDER}/channels`,
      element: <ChannelComponent />,
    },
    {
      path: `${process.env.REACT_APP_SUBFOLDER}/channel-groups`,
      element: <ChannelGroupComponent />,
    },
    {
      path: `${process.env.REACT_APP_SUBFOLDER}/organisation`,
      element: <OrganisationComponent />,
    },
    {
      path: `${process.env.REACT_APP_SUBFOLDER}/title-exclusions`,
      element: <TitleExclusionComponent />,
    },
    {
      path: `${process.env.REACT_APP_SUBFOLDER}/attributes-override`,
      element: <AttributesOverrideComponent />,
    },
    {
      path: '*',
      element: <Navigate to={`${process.env.REACT_APP_SUBFOLDER}/not-found`} />,
    },
    {
      path: `${process.env.REACT_APP_SUBFOLDER}/login`,
      element: <></>,
    },
    {
      path: `${process.env.REACT_APP_SUBFOLDER}/playlist`,
      element: <Playlist />,
    },
    {
      path: `${process.env.REACT_APP_SUBFOLDER}/inclusion`,
      element: <InclusionComponent />,
    },
    {
      path: `${process.env.REACT_APP_SUBFOLDER}/library/programs`,
      element: <ProgramLibraryComponent />,
    },
    {
      path: `${process.env.REACT_APP_SUBFOLDER}/rights`,
      element: <RightsComponent />,
    },
    {
      path: `${process.env.REACT_APP_SUBFOLDER}/library/interstitials`,
      element: <InterstitialsComponent />,
    },
    {
      path: `${process.env.REACT_APP_SUBFOLDER}/content-types`,
      element: <ContentTypesComponent />,
    },
    {
      path: `${process.env.REACT_APP_SUBFOLDER}/library/commercials`,
      element: <CommercialsComponent />,
    },
    {
      path: `${process.env.REACT_APP_SUBFOLDER}/advanced-schedule`,
      element: (
        <ProtectedRoute>
          <AdvancedScheduleComponent />
        </ProtectedRoute>
      ),
    },
    {
      path: `${process.env.REACT_APP_SUBFOLDER}/standby-schedule`,
      element: (
        <ProtectedRoute>
          <StandbyScheduleComponent />
        </ProtectedRoute>
      ),
    },
    {
      path: `${process.env.REACT_APP_SUBFOLDER}/presentation-schedule`,
      element: (
        <ProtectedRoute>
          <PresentationScheduleComponent />
        </ProtectedRoute>
      ),
    },
    {
      path: `${process.env.REACT_APP_SUBFOLDER}/notifications`,
      element: (
        <ProtectedRoute>
          <NotificationsComponent />
        </ProtectedRoute>
      ),
    },
    {
      path: `${process.env.REACT_APP_SUBFOLDER}/monitoring`,
      element: (
        <ProtectedRoute>
          <MonitoringComponent />
        </ProtectedRoute>
      ),
    },
    {
      path: `${process.env.REACT_APP_SUBFOLDER}/new-unified-schedule`,
      element: (
        <ProtectedRoute>
          <UnifiedScheduleComponent
            config={unifiedScheduleConfigurations.new}
          />
        </ProtectedRoute>
      ),
    },
    {
      path: `${process.env.REACT_APP_SUBFOLDER}/advanced-unified-schedule`,
      element: (
        <ProtectedRoute>
          <UnifiedScheduleComponent
            config={unifiedScheduleConfigurations.advanced}
          />
        </ProtectedRoute>
      ),
    },
  ];
}

const viewsRouter = new ViewsRouter();
export default viewsRouter;
